import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from '../../hooks/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  Users, 
  Download, 
  Search, 
  RefreshCw,
  FileText,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Clock,
  CheckCircle2,
  XCircle,
  MessageSquare,
  UserCheck,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Eye,
  Trash2,
  Building2,
  Send,
  AlertCircle,
  X,
  ShieldCheck,
  Lock
} from 'lucide-react';
import AdminEmailTemplates from './AdminEmailTemplates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  
  const [activeTab, setActiveTab] = useState('talent-pool');
  
  // Talent Pool State
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ professions: [], countries: [], statuses: [] });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [professionFilter, setProfessionFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');
  const [page, setPage] = useState(1);
  const limit = 20;
  
  // Contact Requests State
  const [contactRequests, setContactRequests] = useState([]);
  const [contactStats, setContactStats] = useState(null);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactStatusFilter, setContactStatusFilter] = useState('all');
  const [contactPage, setContactPage] = useState(1);
  const [contactTotal, setContactTotal] = useState(0);
  
  // Modal states
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showRequestDetails, setShowRequestDetails] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');

  // Get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  // Check authentication on mount
  useEffect(() => {
    checkAuth();
  }, []);

  // Fetch data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchStats();
      fetchEntries();
    }
  }, [isAuthenticated, statusFilter, professionFilter, countryFilter, page]);

  useEffect(() => {
    if (isAuthenticated && activeTab === 'contact-requests') {
      fetchContactRequests();
      fetchContactStats();
    }
  }, [isAuthenticated, activeTab, contactStatusFilter, contactPage]);

  const checkAuth = async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      setAuthLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/verify`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem('adminToken');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('adminToken');
    }
    setAuthLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }

      localStorage.setItem('adminToken', data.access_token);
      localStorage.setItem('userType', 'admin');
      localStorage.setItem('userName', data.user_name);
      setIsAuthenticated(true);
      toast({ title: 'Login Successful', description: 'Welcome to the Admin Dashboard' });
    } catch (error) {
      toast({ 
        title: 'Login Failed', 
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('userType');
    localStorage.removeItem('userName');
    setIsAuthenticated(false);
    toast({ title: 'Logged Out', description: 'You have been logged out' });
  };

  // ============ TALENT POOL FUNCTIONS ============
  
  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/talent-pool/stats`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch stats');
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchEntries = async () => {
    try {
      setLoading(true);
      
      let url = `${API_URL}/api/admin/talent-pool?skip=${(page - 1) * limit}&limit=${limit}`;
      
      if (statusFilter && statusFilter !== 'all') url += `&status=${statusFilter}`;
      if (professionFilter && professionFilter !== 'all') url += `&profession=${encodeURIComponent(professionFilter)}`;
      if (countryFilter && countryFilter !== 'all') url += `&country=${encodeURIComponent(countryFilter)}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      
      const response = await fetch(url, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed to fetch entries');
      
      const data = await response.json();
      setEntries(data.entries);
      setTotal(data.total);
      setFilters(data.filters);
    } catch (error) {
      console.error('Error fetching entries:', error);
      toast({ title: 'Error', description: 'Failed to load talent pool data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchEntries();
  };

  const updateStatus = async (entryId, newStatus, notes = '') => {
    try {
      let url = `${API_URL}/api/admin/talent-pool/${entryId}/status?status=${newStatus}`;
      if (notes) url += `&notes=${encodeURIComponent(notes)}`;
      
      const response = await fetch(url, { 
        method: 'PUT',
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to update status');
      
      toast({ title: 'Status Updated', description: `Candidate marked as ${newStatus}` });
      fetchEntries();
      fetchStats();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' });
    }
  };

  const deleteEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/talent-pool/${entryId}`, { 
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to delete');
      
      toast({ title: 'Deleted', description: 'Entry removed from talent pool' });
      fetchEntries();
      fetchStats();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete entry', variant: 'destructive' });
    }
  };

  const exportCSV = () => {
    let url = `${API_URL}/api/admin/talent-pool/export?`;
    if (statusFilter && statusFilter !== 'all') url += `&status=${statusFilter}`;
    if (professionFilter && professionFilter !== 'all') url += `&profession=${encodeURIComponent(professionFilter)}`;
    if (countryFilter && countryFilter !== 'all') url += `&country=${encodeURIComponent(countryFilter)}`;
    
    // For export, we'll use the legacy key as a fallback since download doesn't support Bearer easily
    url += `&admin_key=luma-admin-2024`;
    
    toast({ 
      title: 'Downloading...', 
      description: 'Your Excel file will download shortly' 
    });
    window.open(url, '_blank');
  };

  const downloadCV = (entryId, candidateName) => {
    const url = `${API_URL}/api/admin/talent-pool/${entryId}/download-cv?admin_key=luma-admin-2024`;
    toast({ 
      title: 'Downloading CV', 
      description: `Downloading CV for ${candidateName}` 
    });
    window.open(url, '_blank');
  };

  // ============ CONTACT REQUESTS FUNCTIONS ============

  const fetchContactStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/contact-requests/stats`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch contact stats');
      const data = await response.json();
      setContactStats(data);
    } catch (error) {
      console.error('Error fetching contact stats:', error);
    }
  };

  const fetchContactRequests = async () => {
    try {
      setContactLoading(true);
      
      let url = `${API_URL}/api/admin/contact-requests?skip=${(contactPage - 1) * limit}&limit=${limit}`;
      if (contactStatusFilter && contactStatusFilter !== 'all') url += `&status=${contactStatusFilter}`;
      
      const response = await fetch(url, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed to fetch contact requests');
      
      const data = await response.json();
      setContactRequests(data.requests);
      setContactTotal(data.total);
    } catch (error) {
      console.error('Error fetching contact requests:', error);
      toast({ title: 'Error', description: 'Failed to load contact requests', variant: 'destructive' });
    } finally {
      setContactLoading(false);
    }
  };

  const updateRequestStatus = async (requestId, newStatus) => {
    try {
      let url = `${API_URL}/api/admin/contact-requests/${requestId}/status?status=${newStatus}`;
      if (adminNotes) url += `&admin_notes=${encodeURIComponent(adminNotes)}`;
      
      const response = await fetch(url, { 
        method: 'PUT',
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to update status');
      
      toast({ title: 'Status Updated', description: `Request marked as ${newStatus}` });
      fetchContactRequests();
      fetchContactStats();
      setShowRequestDetails(false);
      setAdminNotes('');
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' });
    }
  };

  const deleteRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this request?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/contact-requests/${requestId}`, { 
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to delete');
      
      toast({ title: 'Deleted', description: 'Request removed' });
      fetchContactRequests();
      fetchContactStats();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete request', variant: 'destructive' });
    }
  };

  // ============ HELPERS ============

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-100 text-blue-700 border-blue-200',
      contacted: 'bg-amber-100 text-amber-700 border-amber-200',
      interviewed: 'bg-purple-100 text-purple-700 border-purple-200',
      hired: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      rejected: 'bg-red-100 text-red-700 border-red-200',
      pending: 'bg-amber-100 text-amber-700 border-amber-200',
      approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      completed: 'bg-teal-100 text-teal-700 border-teal-200'
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  const totalPages = Math.ceil(total / limit);
  const contactTotalPages = Math.ceil(contactTotal / limit);

  // Loading state
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-teal-600 mx-auto mb-4" />
          <p className="text-gray-600">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  // Login form
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center px-4">
        <Card className="w-full max-w-md border-2 border-gray-700 bg-gray-800/50 backdrop-blur">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShieldCheck className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-2xl text-white">Admin Login</CardTitle>
            <p className="text-gray-400 text-sm mt-2">
              Secure access to the Luma Med Global admin dashboard
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-gray-300">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  placeholder="admin@lumamedglobal.com"
                  required
                  className="mt-1 bg-gray-700 border-gray-600 text-white placeholder:text-gray-500"
                  data-testid="admin-login-email"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-gray-300">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="mt-1 bg-gray-700 border-gray-600 text-white placeholder:text-gray-500"
                  data-testid="admin-login-password"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700"
                disabled={loginLoading}
                data-testid="admin-login-submit"
              >
                {loginLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    <Lock className="h-4 w-4 mr-2" />
                    Sign In to Admin
                  </>
                )}
              </Button>
            </form>
            
            <div className="mt-6 pt-6 border-t border-gray-700">
              <p className="text-xs text-gray-500 text-center">
                Protected by JWT authentication. Only authorised administrators can access this dashboard.
              </p>
            </div>
            
            <div className="mt-4 text-center">
              <Link to="/" className="text-sm text-teal-400 hover:text-teal-300">
                ← Back to Homepage
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main dashboard (authenticated)
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="flex items-center space-x-3 mb-1">
                <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                  <ShieldCheck className="h-3 w-3 mr-1" />
                  Authenticated
                </Badge>
              </div>
              <p className="text-gray-600">Manage talent pool and contact requests</p>
            </div>
            <div className="flex items-center space-x-3">
              <Button variant="outline" onClick={() => { fetchEntries(); fetchStats(); fetchContactRequests(); fetchContactStats(); }}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button variant="outline" onClick={handleLogout} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                Logout
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full max-w-xl grid-cols-3">
              <TabsTrigger value="talent-pool" className="flex items-center space-x-2">
                <Users className="h-4 w-4" />
                <span>Talent Pool</span>
                {stats && <Badge variant="secondary" className="ml-2">{stats.total}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="contact-requests" className="flex items-center space-x-2">
                <MessageSquare className="h-4 w-4" />
                <span>Contact Requests</span>
                {contactStats && contactStats.pending > 0 && (
                  <Badge className="ml-2 bg-red-500">{contactStats.pending}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="email-templates" className="flex items-center space-x-2" data-testid="email-templates-tab">
                <Mail className="h-4 w-4" />
                <span>Email Templates</span>
              </TabsTrigger>
            </TabsList>

            {/* ============ TALENT POOL TAB ============ */}
            <TabsContent value="talent-pool" className="space-y-6">
              {/* Stats Cards */}
              {stats && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <Card className="border-2 border-blue-200 bg-blue-50">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-blue-600 font-medium">Total</p>
                          <p className="text-2xl font-bold text-blue-700">{stats.total}</p>
                        </div>
                        <Users className="h-8 w-8 text-blue-400" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-2 border-amber-200 bg-amber-50">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-amber-600 font-medium">New</p>
                          <p className="text-2xl font-bold text-amber-700">{stats.by_status.new}</p>
                        </div>
                        <Clock className="h-8 w-8 text-amber-400" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-2 border-purple-200 bg-purple-50">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-purple-600 font-medium">Contacted</p>
                          <p className="text-2xl font-bold text-purple-700">{stats.by_status.contacted}</p>
                        </div>
                        <MessageSquare className="h-8 w-8 text-purple-400" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-2 border-cyan-200 bg-cyan-50">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-cyan-600 font-medium">Interviewed</p>
                          <p className="text-2xl font-bold text-cyan-700">{stats.by_status.interviewed}</p>
                        </div>
                        <UserCheck className="h-8 w-8 text-cyan-400" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-2 border-emerald-200 bg-emerald-50">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-emerald-600 font-medium">Hired</p>
                          <p className="text-2xl font-bold text-emerald-700">{stats.by_status.hired}</p>
                        </div>
                        <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Filters */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex flex-wrap gap-4 items-end">
                    <div className="flex-1 min-w-[200px]">
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Search</label>
                      <form onSubmit={handleSearch} className="flex space-x-2">
                        <Input placeholder="Search by name, email, specialisation..." value={search} onChange={(e) => setSearch(e.target.value)} className="flex-1" />
                        <Button type="submit" variant="outline"><Search className="h-4 w-4" /></Button>
                      </form>
                    </div>
                    
                    <div className="w-40">
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Status</label>
                      <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                        <SelectTrigger><SelectValue placeholder="All Statuses" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Statuses</SelectItem>
                          {filters.statuses.filter(s => s).map(s => (
                            <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="w-48">
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Profession</label>
                      <Select value={professionFilter} onValueChange={(v) => { setProfessionFilter(v); setPage(1); }}>
                        <SelectTrigger><SelectValue placeholder="All Professions" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Professions</SelectItem>
                          {filters.professions.filter(p => p).map(p => (
                            <SelectItem key={p} value={p}>{p}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="w-44">
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Country</label>
                      <Select value={countryFilter} onValueChange={(v) => { setCountryFilter(v); setPage(1); }}>
                        <SelectTrigger><SelectValue placeholder="All Countries" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Countries</SelectItem>
                          {filters.countries.filter(c => c).map(c => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <Button onClick={exportCSV} className="bg-teal-600 hover:bg-teal-700" data-testid="export-excel-btn">
                      <Download className="h-4 w-4 mr-2" />
                      Export to Excel
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Table */}
              <Card>
                <CardContent className="p-0">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
                    </div>
                  ) : entries.length === 0 ? (
                    <div className="text-center py-12">
                      <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No entries found</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Contact</TableHead>
                          <TableHead>Profession</TableHead>
                          <TableHead>Country</TableHead>
                          <TableHead>Experience</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Submitted</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {entries.map((entry) => (
                          <TableRow key={entry.id} className="hover:bg-gray-50">
                            <TableCell>
                              <div>
                                <p className="font-medium text-gray-900">{entry.first_name} {entry.last_name}</p>
                                <p className="text-sm text-gray-500">{entry.specialisation}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <p className="flex items-center text-gray-600"><Mail className="h-3 w-3 mr-1" />{entry.email}</p>
                                <p className="flex items-center text-gray-500"><Phone className="h-3 w-3 mr-1" />{entry.phone}</p>
                              </div>
                            </TableCell>
                            <TableCell><Badge variant="outline">{entry.profession}</Badge></TableCell>
                            <TableCell><span className="flex items-center text-sm text-gray-600"><MapPin className="h-3 w-3 mr-1" />{entry.country}</span></TableCell>
                            <TableCell><span className="text-sm text-gray-600">{entry.years_experience} years</span></TableCell>
                            <TableCell>
                              <Select value={entry.status} onValueChange={(v) => updateStatus(entry.id, v)}>
                                <SelectTrigger className={`w-32 h-8 text-xs ${getStatusBadge(entry.status)}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="new">New</SelectItem>
                                  <SelectItem value="contacted">Contacted</SelectItem>
                                  <SelectItem value="interviewed">Interviewed</SelectItem>
                                  <SelectItem value="hired">Hired</SelectItem>
                                  <SelectItem value="rejected">Rejected</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell><span className="text-sm text-gray-500">{new Date(entry.submitted_at).toLocaleDateString()}</span></TableCell>
                            <TableCell>
                              <div className="flex items-center space-x-2">
                                {entry.cv_url && (
                                  <Button variant="ghost" size="sm" onClick={() => downloadCV(entry.id, `${entry.first_name} ${entry.last_name}`)} title="Download CV" data-testid={`download-cv-${entry.id}`}>
                                    <FileText className="h-4 w-4 text-teal-600" />
                                  </Button>
                                )}
                                <Button variant="ghost" size="sm" onClick={() => { setSelectedEntry(entry); setShowDetails(true); }} title="View Details">
                                  <Eye className="h-4 w-4 text-gray-600" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => deleteEntry(entry.id)} title="Delete">
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                  
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t">
                      <p className="text-sm text-gray-600">Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} entries</p>
                      <div className="flex items-center space-x-2">
                        <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}><ChevronLeft className="h-4 w-4" /></Button>
                        <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
                        <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}><ChevronRight className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* ============ CONTACT REQUESTS TAB ============ */}
            <TabsContent value="contact-requests" className="space-y-6">
              {/* Stats Cards */}
              {contactStats && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <Card className="border-2 border-blue-200 bg-blue-50">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-blue-600 font-medium">Total Requests</p>
                          <p className="text-2xl font-bold text-blue-700">{contactStats.total}</p>
                        </div>
                        <MessageSquare className="h-8 w-8 text-blue-400" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-2 border-amber-200 bg-amber-50">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-amber-600 font-medium">Pending</p>
                          <p className="text-2xl font-bold text-amber-700">{contactStats.pending}</p>
                        </div>
                        <AlertCircle className="h-8 w-8 text-amber-400" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-2 border-emerald-200 bg-emerald-50">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-emerald-600 font-medium">Approved</p>
                          <p className="text-2xl font-bold text-emerald-700">{contactStats.approved}</p>
                        </div>
                        <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-2 border-red-200 bg-red-50">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-red-600 font-medium">Rejected</p>
                          <p className="text-2xl font-bold text-red-700">{contactStats.rejected}</p>
                        </div>
                        <XCircle className="h-8 w-8 text-red-400" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-2 border-teal-200 bg-teal-50">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-teal-600 font-medium">This Week</p>
                          <p className="text-2xl font-bold text-teal-700">{contactStats.this_week}</p>
                        </div>
                        <Clock className="h-8 w-8 text-teal-400" />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Filter */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-48">
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Status Filter</label>
                      <Select value={contactStatusFilter} onValueChange={(v) => { setContactStatusFilter(v); setContactPage(1); }}>
                        <SelectTrigger><SelectValue placeholder="All Statuses" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Statuses</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="approved">Approved</SelectItem>
                          <SelectItem value="rejected">Rejected</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Contact Requests Table */}
              <Card>
                <CardContent className="p-0">
                  {contactLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
                    </div>
                  ) : contactRequests.length === 0 ? (
                    <div className="text-center py-12">
                      <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No contact requests yet</p>
                      <p className="text-sm text-gray-400 mt-1">Contact form submissions and employer requests will appear here</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Type</TableHead>
                          <TableHead>From</TableHead>
                          <TableHead>Contact Info</TableHead>
                          <TableHead>Subject / Message</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {contactRequests.map((request) => (
                          <TableRow key={request.id} className="hover:bg-gray-50">
                            <TableCell>
                              <Badge variant="outline" className={request.type === 'general' ? 'border-blue-300 text-blue-700' : 'border-purple-300 text-purple-700'}>
                                {request.type === 'general' ? 'Contact Form' : 'Employer Request'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {request.type === 'general' ? (
                                <div>
                                  <p className="font-medium text-gray-900">{request.name}</p>
                                  <p className="text-sm text-gray-500">Website Visitor</p>
                                </div>
                              ) : (
                                <div>
                                  <div className="flex items-center space-x-2">
                                    <Building2 className="h-4 w-4 text-gray-400" />
                                    <span className="font-medium text-gray-900">{request.employer_name || 'Unknown'}</span>
                                  </div>
                                  <p className="text-sm text-gray-500">Re: {request.candidate_name}</p>
                                </div>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <p className="flex items-center text-gray-600">
                                  <Mail className="h-3 w-3 mr-1" />
                                  {request.type === 'general' ? request.email : request.candidate_email}
                                </p>
                                {request.candidate_phone && request.type !== 'general' && (
                                  <p className="flex items-center text-gray-500"><Phone className="h-3 w-3 mr-1" />{request.candidate_phone}</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {request.type === 'general' && request.subject && (
                                <p className="text-sm font-medium text-gray-800 mb-1">{request.subject}</p>
                              )}
                              <p className="text-sm text-gray-600 max-w-xs truncate">{request.message || 'No message'}</p>
                            </TableCell>
                            <TableCell>
                              <Badge className={getStatusBadge(request.status)}>
                                {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-gray-500">{request.created_at ? new Date(request.created_at).toLocaleDateString() : 'N/A'}</span>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center space-x-2">
                                <Button variant="ghost" size="sm" onClick={() => { setSelectedRequest(request); setAdminNotes(request.admin_notes || ''); setShowRequestDetails(true); }} title="View & Manage">
                                  <Eye className="h-4 w-4 text-gray-600" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => deleteRequest(request.id)} title="Delete">
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                  
                  {contactTotalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t">
                      <p className="text-sm text-gray-600">Showing {((contactPage - 1) * limit) + 1} to {Math.min(contactPage * limit, contactTotal)} of {contactTotal} requests</p>
                      <div className="flex items-center space-x-2">
                        <Button variant="outline" size="sm" onClick={() => setContactPage(p => Math.max(1, p - 1))} disabled={contactPage === 1}><ChevronLeft className="h-4 w-4" /></Button>
                        <span className="text-sm text-gray-600">Page {contactPage} of {contactTotalPages}</span>
                        <Button variant="outline" size="sm" onClick={() => setContactPage(p => Math.min(contactTotalPages, p + 1))} disabled={contactPage === contactTotalPages}><ChevronRight className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* ============ EMAIL TEMPLATES TAB ============ */}
            <TabsContent value="email-templates">
              <AdminEmailTemplates />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Talent Pool Details Modal */}
      {showDetails && selectedEntry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Candidate Details</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowDetails(false)}><X className="h-5 w-5" /></Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div><label className="text-sm font-medium text-gray-500">Full Name</label><p className="text-lg font-semibold">{selectedEntry.first_name} {selectedEntry.last_name}</p></div>
                <div><label className="text-sm font-medium text-gray-500">Status</label><p><Badge className={getStatusBadge(selectedEntry.status)}>{selectedEntry.status}</Badge></p></div>
                <div><label className="text-sm font-medium text-gray-500">Email</label><p className="text-gray-900">{selectedEntry.email}</p></div>
                <div><label className="text-sm font-medium text-gray-500">Phone</label><p className="text-gray-900">{selectedEntry.phone}</p></div>
                <div><label className="text-sm font-medium text-gray-500">Profession</label><p className="text-gray-900">{selectedEntry.profession}</p></div>
                <div><label className="text-sm font-medium text-gray-500">Specialisation</label><p className="text-gray-900">{selectedEntry.specialisation}</p></div>
                <div><label className="text-sm font-medium text-gray-500">Country</label><p className="text-gray-900">{selectedEntry.country}</p></div>
                <div><label className="text-sm font-medium text-gray-500">Experience</label><p className="text-gray-900">{selectedEntry.years_experience} years</p></div>
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t">
                {selectedEntry.cv_url && (
                  <Button variant="outline" onClick={() => downloadCV(selectedEntry.id, `${selectedEntry.first_name} ${selectedEntry.last_name}`)} data-testid="modal-download-cv">
                    <Download className="h-4 w-4 mr-2" />Download CV
                  </Button>
                )}
                <Button onClick={() => window.open(`mailto:${selectedEntry.email}`, '_blank')} className="bg-teal-600 hover:bg-teal-700">
                  <Mail className="h-4 w-4 mr-2" />Send Email
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Contact Request Details Modal */}
      {showRequestDetails && selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Contact Request Details</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowRequestDetails(false)}><X className="h-5 w-5" /></Button>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Employer Info */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2 flex items-center"><Building2 className="h-4 w-4 mr-2" />Employer</h4>
                <p className="text-blue-800">{selectedRequest.employer_name || 'Unknown Employer'}</p>
              </div>
              
              {/* Candidate Info */}
              <div className="p-4 bg-teal-50 rounded-lg">
                <h4 className="font-semibold text-teal-900 mb-2 flex items-center"><Users className="h-4 w-4 mr-2" />Candidate</h4>
                <div className="space-y-1">
                  <p className="text-teal-800 font-medium">{selectedRequest.candidate_name}</p>
                  <p className="text-teal-700 text-sm">{selectedRequest.candidate_profession}</p>
                  <p className="text-teal-700 text-sm flex items-center"><Mail className="h-3 w-3 mr-1" />{selectedRequest.candidate_email}</p>
                  {selectedRequest.candidate_phone && (
                    <p className="text-teal-700 text-sm flex items-center"><Phone className="h-3 w-3 mr-1" />{selectedRequest.candidate_phone}</p>
                  )}
                </div>
              </div>
              
              {/* Message */}
              {selectedRequest.message && (
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Employer&apos;s Message</label>
                  <p className="p-3 bg-gray-50 rounded-lg text-gray-700">{selectedRequest.message}</p>
                </div>
              )}
              
              {/* Admin Notes */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Admin Notes</label>
                <Textarea 
                  placeholder="Add notes about this request (e.g., contacted candidate on X date, waiting for response...)"
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  rows={3}
                />
              </div>
              
              {/* Status Info */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">Current Status</p>
                  <Badge className={getStatusBadge(selectedRequest.status)}>{selectedRequest.status}</Badge>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Requested</p>
                  <p className="text-sm text-gray-700">{new Date(selectedRequest.created_at).toLocaleString()}</p>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 pt-4 border-t">
                <Button variant="outline" className="flex-1" onClick={() => window.open(`mailto:${selectedRequest.candidate_email}?subject=Opportunity from ${selectedRequest.employer_name}`, '_blank')}>
                  <Mail className="h-4 w-4 mr-2" />Email Candidate
                </Button>
                
                {selectedRequest.status === 'pending' && (
                  <>
                    <Button onClick={() => updateRequestStatus(selectedRequest.id, 'approved')} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                      <CheckCircle2 className="h-4 w-4 mr-2" />Approve
                    </Button>
                    <Button onClick={() => updateRequestStatus(selectedRequest.id, 'rejected')} variant="destructive" className="flex-1">
                      <XCircle className="h-4 w-4 mr-2" />Reject
                    </Button>
                  </>
                )}
                
                {selectedRequest.status === 'approved' && (
                  <Button onClick={() => updateRequestStatus(selectedRequest.id, 'completed')} className="flex-1 bg-teal-600 hover:bg-teal-700">
                    <CheckCircle2 className="h-4 w-4 mr-2" />Mark Completed
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
